import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, AlertCircle, Search, ShoppingCart } from 'lucide-react';
import FallbackImage from '../components/FallbackImage';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const ProgrammingProductDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);

  const decodedId = decodeURIComponent(id);

  useEffect(() => { fetchProduct(); }, [decodedId]);

  useEffect(() => {
    if (product && product.colors && product.colors.length > 0) {
      setSelectedColor(product.colors[0]);
    }
  }, [product]);

  // Search programming products as user types
  useEffect(() => {
    const searchProducts = async () => {
      if (searchQuery.length < 2 || !product?.season) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/programming/products?season=${product.season}&search=${encodeURIComponent(searchQuery)}&limit=8`, { headers });
        setSearchResults(response.data.slice(0, 8));
        setShowSearchResults(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, product?.season]);

  const handleSearchSelect = (productId) => {
    setSearchQuery('');
    setShowSearchResults(false);
    setSearchResults([]);
    navigate(`/programming/product/${encodeURIComponent(productId)}`);
  };

  const fetchProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/programming/products/${encodeURIComponent(decodedId)}`, { headers });
      setProduct(response.data);
    } catch (error) {
      toast.error('Error al cargar producto');
    } finally {
      setLoading(false);
    }
  };

  const formatServiceDate = (dateStr) => {
    if (!dateStr) return 'consultar';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 'consultar';
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const priceFor = (size) => (((user && (user.role === 'admin' || user.approved) && size.pt_price) ? size.pt_price : size.pvpr_price) || 0);
  const keyOf = (sz) => sz.ean || sz.size;
  const setQty = (size, val) => {
    const q = Math.max(0, isNaN(val) ? 0 : val);
    setQuantities((prev) => ({ ...prev, [keyOf(size)]: q }));
  };
  const totalUnits = () => (selectedColor?.sizes || []).reduce((s, sz) => s + (quantities[keyOf(sz)] || 0), 0);
  const subtotal = () => (selectedColor?.sizes || []).reduce((s, sz) => s + (quantities[keyOf(sz)] || 0) * priceFor(sz), 0);

  const addToCart = () => {
    if (!user) {
      toast.error('Debes iniciar sesión para hacer pedidos');
      navigate('/login');
      return;
    }
    if (user.role !== 'admin' && user.role !== 'manager' && !user.approved) {
      toast.error('Tu cuenta debe ser aprobada para hacer pedidos de programación');
      return;
    }
    const rows = (selectedColor?.sizes || []).filter((sz) => (quantities[keyOf(sz)] || 0) > 0);
    if (rows.length === 0) {
      toast.error('Introduce al menos una cantidad');
      return;
    }
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    rows.forEach((sz) => {
      const qty = quantities[keyOf(sz)];
      const uniqueVariantId = sz.ean || `${product.id}-${selectedColor.color}-${sz.size}`;
      const idx = cart.findIndex((item) => item.variant_id === uniqueVariantId && item.is_programming);
      const cartItem = {
        variant_id: uniqueVariantId,
        reference: sz.reference,
        ean: sz.ean,
        model_name: product.model_name,
        color: selectedColor.color,
        size: sz.size,
        quantity: qty,
        price: priceFor(sz),
        image: (product.images && product.images[0]) || '',
        is_programming: true,
        season: product.season,
        service_date: product.service_date
      };
      if (idx !== -1) cart[idx].quantity += qty;
      else cart.push(cartItem);
    });
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdate'));
    toast.success(`¡${rows.length} talla(s) añadidas al carrito de programación!`);
    setQuantities({});
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen"><p className="text-gray-600 text-lg">Cargando...</p></div>;
  }
  if (!product) {
    return <div className="flex justify-center items-center min-h-screen"><p className="text-gray-600 text-lg">Producto no encontrado</p></div>;
  }

  const isPending = user && user.role !== 'admin' && user.role !== 'manager' && !user.approved;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to={`/programming/${product.season || ''}`} className="inline-flex items-center text-white hover:text-gray-300">
              <ArrowLeft className="w-5 h-5 mr-2" />
              <span className="hidden sm:inline">Volver a</span> {product.season}
            </Link>
            
            {/* Quick Search - Hidden on mobile */}
            <div className="relative flex-1 max-w-md mx-4 sm:mx-8 hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar otro modelo o referencia..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                  className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500"
                  data-testid="programming-detail-search"
                />
              </div>
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  {searchResults.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleSearchSelect(result.id)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 border-b border-gray-100 last:border-0 text-left"
                    >
                      <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        {result.images && result.images[0] ? (
                          <img src={result.images[0]} alt={result.model_name || result.model} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ShoppingCart className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{result.model_name || result.model}</p>
                        <p className="text-sm text-blue-600">REF: {result.reference || '-'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Click outside to close */}
              {showSearchResults && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowSearchResults(false)}
                />
              )}
            </div>
            
            <Link to="/" className="flex items-center space-x-2">
              <img src="/hi-tec-logo.png" alt="HI-TEC" className="h-8" />
              <span className="font-bold">B2B</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="bg-blue-600 text-white rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white text-blue-600 px-3 py-1 rounded-full font-bold">{product.season}</div>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              <span className="font-semibold">Disponible desde: {formatServiceDate(product.service_date)}</span>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl mb-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-amber-900 font-semibold">Producto de Programación - Sin Stock Actual</p>
              <p className="text-amber-800 text-sm mt-1">Este pedido se servirá a partir de la fecha indicada. No es un producto con disponibilidad inmediata.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <FallbackImage images={product.images} alt={product.model_name} className="w-full h-[500px] object-cover" />
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.model_name}</h1>
              <p className="text-lg text-gray-600">{product.color}</p>
            </div>

            {selectedColor && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">Tallas y cantidades {selectedColor.color ? `· ${selectedColor.color}` : ''}</h3>
                  <span className="text-xs uppercase tracking-wide text-gray-400 hidden sm:inline">Introduce todas las cantidades del tirón</span>
                </div>
                <div className="border border-gray-200 rounded-xl overflow-x-auto bg-white">
                  <table className="w-full min-w-[320px] text-sm" data-testid="sizes-table">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                      <tr>
                        <th className="text-left px-2 sm:px-4 py-3">Talla</th>
                        <th className="text-left px-2 sm:px-4 py-3 hidden sm:table-cell">Estado</th>
                        <th className="text-left px-2 sm:px-4 py-3">Precio</th>
                        <th className="text-right px-2 sm:px-4 py-3">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedColor.sizes.map((sz) => {
                        const q = quantities[keyOf(sz)] || 0;
                        return (
                          <tr key={keyOf(sz)} className="border-t border-gray-100" data-testid={`size-row-${sz.size}`}>
                            <td className="px-2 sm:px-4 py-3 font-bold text-base">{sz.size}</td>
                            <td className="px-2 sm:px-4 py-3 hidden sm:table-cell"><span className="text-blue-600 font-medium">PROGRAMACIÓN</span></td>
                            <td className="px-2 sm:px-4 py-3">{user ? `€${priceFor(sz).toFixed(2)}` : '—'}</td>
                            <td className="px-2 sm:px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  className="h-10 w-10 sm:h-8 sm:w-8 text-lg font-bold touch-manipulation" 
                                  disabled={q <= 0}
                                  data-testid={`qty-dec-${sz.size}`} 
                                  onClick={() => setQty(sz, q - 1)}
                                >−</Button>
                                <input 
                                  type="text" 
                                  inputMode="numeric" 
                                  pattern="[0-9]*"
                                  min="0" 
                                  value={q}
                                  data-testid={`qty-input-${sz.size}`}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9]/g, '');
                                    setQty(sz, parseInt(val, 10) || 0);
                                  }}
                                  onFocus={(e) => e.target.select()}
                                  className="w-16 sm:w-14 text-center text-lg font-semibold border-2 border-gray-300 rounded-md py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 touch-manipulation" 
                                />
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  className="h-10 w-10 sm:h-8 sm:w-8 text-lg font-bold touch-manipulation"
                                  data-testid={`qty-inc-${sz.size}`} 
                                  onClick={() => setQty(sz, q + 1)}
                                >+</Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="bg-black text-white rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-300">Total unidades</p>
                <p className="text-2xl font-bold" data-testid="total-units">{totalUnits()}</p>
              </div>
              {user && (
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-gray-300">Subtotal</p>
                  <p className="text-2xl font-bold" data-testid="subtotal">€{subtotal().toFixed(2)}</p>
                </div>
              )}
            </div>

            {isPending ? (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-xl p-4 text-center">
                <p className="text-yellow-900 font-semibold mb-2">⏳ Cuenta Pendiente de Aprobación</p>
                <p className="text-sm text-yellow-800">No puedes realizar pedidos de programación hasta que tu cuenta sea aprobada.</p>
              </div>
            ) : (
              <Button
                data-testid="add-to-cart-btn"
                onClick={addToCart}
                disabled={totalUnits() === 0}
                className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg text-white disabled:bg-gray-400"
              >
                {totalUnits() === 0 ? 'Añadir al carrito (introduce cantidades)' : `Añadir ${totalUnits()} unidad(es) a Programación`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgrammingProductDetail;
